import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";


// Context
import { useCoursesNewContext } from "../../context";

// Components
import CustomButton from "../../../../../components/CustomButton";
import AttachMaterials from "../../../../../components/AttachMaterials";

// Styles
import styles from "./style.module.scss";
import stepsStyles from "../steps.module.scss";

// Types
import { CourseData } from "../../types";
import { Link, UploadFile } from "../../../../../types";
import Typography from "../../../../../components/Typography";

interface MaterialsProps {
  onNext: (course: CourseData) => void;
  onPrev: () => void;
}

const Materials: React.FC<MaterialsProps> = ({ onNext, onPrev }) => {
  // Hooks
  const { t } = useTranslation();

  // Context
  const { course, updateCourse, createCourse } = useCoursesNewContext();

  // State
  const [files, setFiles] = useState<string[]>([]);
  const [links, setLinks] = useState<Link[]>([]);

  // Handlers
  const handleNext = useCallback(async () => {
    let _course: CourseData = {
      ...course,
      files: files,
      links: links.filter((link) => link.url !== '').map((link) => link.url)
    };

    let result = null;
    if (!!_course.id) {
      result = await updateCourse(_course);
    } else {
      result = await createCourse(_course);
    }

    if (result.success) onNext(result.data as CourseData);
  }, [onNext, updateCourse, createCourse, course, files, links]);

  const handlePrev = useCallback(() => {
    onPrev();
  }, [onPrev]);

  const handleOnChangeUploadedFiles = useCallback((files_to_set: string[]) => {
    setFiles(files_to_set);
  }, [setFiles])

  // Render
  return (
    <div className={stepsStyles.root}>
      <AttachMaterials
        onChangeUploadedFiles={handleOnChangeUploadedFiles}
        onChangeLinks={(links) => setLinks(links)}
        forcedNote={course?.mainNote}
        classes={{
          root: styles.attach_materials,
        }}
        header={
          <div className={styles.header}>
            <Typography variant='h6' className={styles.header_title}>
              {t('courses.new.materials.title')}
            </Typography>

            <Typography variant='p' className={styles.header_subtitle}>
              {t('courses.new.materials.subtitle')}
            </Typography>
          </div>
        }
      />

      <div className={stepsStyles.actions}>
        <CustomButton
          variant='secondary'
          onClick={handlePrev}
          classes={{
            root: stepsStyles.prevButton,
          }}
        >
          {t('actions.previous')}
        </CustomButton>

        <CustomButton
          variant='primary'
          onClick={handleNext}
          disabled={files.length === 0 && links.length === 0}
          classes={{
            root: stepsStyles.nextButton,
          }}
        >
          {t('actions.next')}
        </CustomButton>
      </div>
    </div>
  );
};

export default Materials;
